import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, getOrganizationSchema } from '../../utils/seo';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';

export default function AGB() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [showBackToTop, setShowBackToTop] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'AGB – Allgemeine Geschäftsbedingungen | Seitenschmied',
    description: 'Allgemeine Geschäftsbedingungen (AGB) von Seitenschmied. Vertragsbedingungen für Website-Erstellung, Hosting und Support-Dienstleistungen.',
    canonical: `${siteUrl}/agb`,
    noindex: true,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/agb#webpage`,
          url: `${siteUrl}/agb`,
          name: 'AGB – Allgemeine Geschäftsbedingungen | Seitenschmied',
          description: 'Allgemeine Geschäftsbedingungen von Seitenschmied für Website-Erstellung, Hosting und Support.',
          inLanguage: 'de-DE',
          publisher: { '@id': `${siteUrl}/#organization` },
          noIndex: true,
        },
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteNav navId="agb-nav" />

      {/* Hero */}
      <div className="relative pt-24 sm:pt-32 pb-12 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full bg-teal-100 text-teal-700 font-bold text-sm mb-4 sm:mb-6">
            <i className="ri-file-text-line text-base sm:text-lg"></i>
            <span>Rechtliches</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Allgemeine Geschäftsbedingungen
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Vertragsbedingungen für unsere Dienstleistungen
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="bg-white rounded-2xl border-2 border-teal-200 shadow-lg p-6 sm:p-8 space-y-6 sm:space-y-8">
          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-line text-sm text-white"></i>
              </span>
              1. Geltungsbereich
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge zwischen Seitenschmied
              (nachfolgend „Auftragnehmer") und dem Kunden (nachfolgend „Auftraggeber") über die Erstellung,
              Gestaltung, Pflege und Wartung von Websites sowie damit verbundene Dienstleistungen.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-code-line text-sm text-white"></i>
              </span>
              2. Vertragsgegenstand
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Auftragnehmer erstellt für den Auftraggeber eine Website nach den vereinbarten Spezifikationen.
              Der genaue Leistungsumfang ergibt sich aus dem gewählten Paket (Essential, Professional oder Enterprise)
              und wird im individuellen Angebot konkretisiert.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              2.1 Leistungsumfang
            </h3>
            <ul className="list-disc list-inside text-gray-600 text-base sm:text-lg space-y-2 ml-4 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <li>Konzeption und Design der Website</li>
              <li>Technische Umsetzung und Programmierung</li>
              <li>Einrichtung des Content-Management-Systems</li>
              <li>Laufender Support gemäß gewähltem Paket</li>
              <li>Optional: SEO-Optimierung, Rechtstexte, zusätzliche Extras</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-money-euro-circle-line text-sm text-white"></i>
              </span>
              3. Preise und Zahlungsbedingungen
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              3.1 Einmalige Einrichtungsgebühr
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die einmalige Einrichtungsgebühr wird nach Vertragsabschluss fällig und ist innerhalb von 14 Tagen zu
              begleichen. Mit Zahlungseingang beginnt die Umsetzung des Projekts.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              3.2 Monatliche Servicegebühr
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die monatliche Servicegebühr wird im Voraus zum Monatsersten fällig und umfasst Hosting, Wartung,
              Updates und Support gemäß gewähltem Paket.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              3.3 Zusatzleistungen
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Zusätzliche Leistungen außerhalb des gebuchten Pakets werden nach Aufwand abgerechnet und separat in
              Rechnung gestellt.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-follow-line text-sm text-white"></i>
              </span>
              4. Mitwirkungspflichten des Auftraggebers
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Auftraggeber verpflichtet sich:
            </p>
            <ul className="list-disc list-inside text-gray-600 text-base sm:text-lg space-y-2 ml-4 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <li>Alle erforderlichen Inhalte, Texte, Bilder und Materialien rechtzeitig bereitzustellen</li>
              <li>Notwendige Zugangsdaten und Informationen zur Verfügung zu stellen</li>
              <li>Feedback und Freigaben innerhalb angemessener Fristen zu erteilen</li>
              <li>Für die Rechtmäßigkeit der bereitgestellten Inhalte zu sorgen</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-line text-sm text-white"></i>
              </span>
              5. Projektablauf und Fristen
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Projektdauer richtet sich nach dem gewählten Paket und wird im Angebot konkretisiert. Verzögerungen
              durch fehlende Mitwirkung des Auftraggebers verlängern die Projektlaufzeit entsprechend.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-copyright-line text-sm text-white"></i>
              </span>
              6. Urheberrecht und Nutzungsrechte
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              6.1 Rechte des Auftragnehmers
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Alle vom Auftragnehmer erstellten Werke (Design, Code, Grafiken) unterliegen dem Urheberrecht. Der
              Auftraggeber erhält nach vollständiger Bezahlung ein einfaches, nicht übertragbares Nutzungsrecht.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              6.2 Rechte des Auftraggebers
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Auftraggeber garantiert, dass alle von ihm bereitgestellten Inhalte frei von Rechten Dritter sind
              und stellt den Auftragnehmer von allen Ansprüchen Dritter frei.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-sm text-white"></i>
              </span>
              7. Gewährleistung und Haftung
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              7.1 Gewährleistung
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Auftragnehmer gewährleistet, dass die Website zum Zeitpunkt der Abnahme frei von wesentlichen
              Mängeln ist. Kleinere optische oder funktionale Abweichungen stellen keinen Mangel dar.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              7.2 Haftung
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Haftung des Auftragnehmers ist auf Vorsatz und grobe Fahrlässigkeit beschränkt. Für leichte
              Fahrlässigkeit haftet der Auftragnehmer nur bei Verletzung wesentlicher Vertragspflichten.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-sm text-white"></i>
              </span>
              8. Vertragslaufzeit und Kündigung
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              8.1 Laufzeit
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Vertrag wird auf unbestimmte Zeit geschlossen. Die Mindestvertragslaufzeit beträgt 12 Monate ab
              Projektabschluss. Nach Ablauf der Mindestvertragslaufzeit verlängert sich der Vertrag automatisch um
              jeweils 1 Monat, sofern er nicht fristgerecht gekündigt wird.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              8.2 Ordentliche Kündigung
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Nach Ablauf der Mindestvertragslaufzeit kann der Vertrag von beiden Parteien mit einer Frist von 3
              Monaten zum Monatsende gekündigt werden.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              8.3 Außerordentliche Kündigung
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt. Ein wichtiger Grund
              liegt insbesondere vor bei Zahlungsverzug von mehr als 2 Monaten.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-keyhole-line text-sm text-white"></i>
              </span>
              9. Datenschutz
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Auftragnehmer verpflichtet sich, alle datenschutzrechtlichen Bestimmungen einzuhalten. Weitere
              Informationen finden Sie in unserer Datenschutzerklärung.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-scales-line text-sm text-white"></i>
              </span>
              10. Schlussbestimmungen
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              10.1 Änderungen der AGB
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Auftragnehmer behält sich vor, diese AGB mit einer Ankündigungsfrist von 4 Wochen zu ändern. Widerspricht
              der Auftraggeber nicht innerhalb von 4 Wochen, gelten die geänderten AGB als akzeptiert.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              10.2 Salvatorische Klausel
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen
              Bestimmungen hiervon unberührt.
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              10.3 Anwendbares Recht und Gerichtsstand
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist der Sitz des Auftragnehmers, sofern
              der Auftraggeber Kaufmann, juristische Person des öffentlichen Rechts oder öffentlich-rechtliches
              Sondervermögen ist.
            </p>
          </section>

          <section className="border-t-2 border-gray-200 pt-6 mt-8">
            <p className="text-gray-600 text-sm sm:text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
              <strong className="text-gray-900">Stand:</strong> Januar 2026
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="mt-8 sm:mt-12 text-center">
          <Link to="/termin-buchen">
            <button
              className="px-8 sm:px-12 py-4 sm:py-5 font-semibold text-base sm:text-lg rounded-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              style={{
                background: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
                color: '#FFFFFF',
                boxShadow: '0 8px 30px rgba(20, 184, 166, 0.3)',
              }}
            >
              Kostenloses Erstgespräch buchen
            </button>
          </Link>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-40 cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
            color: '#FFFFFF',
          }}
        >
          <i className="ri-arrow-up-line text-xl sm:text-2xl"></i>
        </button>
      )}

      <SiteFooter />
    </div>
  );
}